import axios from 'axios';

// Get API base URL from environment variable, with fallback
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080';

// Create axios instance with default configuration
const apiClient = axios.create({
  baseURL: `${API_BASE_URL}/api/v1/ypaas`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add JWT token to requests if available
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('jwt_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Normalize enrollment request shapes into the unified payload:
//   { program_id, enrollees: [{ enrollee_id, custom_responses }] }
function normalizeEnrollPayload(data) {
  if (!data) return data;
  const programId = data.program_id;
  let enrollees = [];
  if (Array.isArray(data.enrollees)) {
    enrollees = data.enrollees.map((e) => ({
      enrollee_id: e.enrollee_id,
      custom_responses: e.custom_responses || {},
    }));
  } else if (Array.isArray(data.enrollee_ids)) {
    // legacy shape
    enrollees = data.enrollee_ids.map((id) => ({ enrollee_id: id, custom_responses: {} }));
  }
  return { program_id: programId, enrollees };
}

// API service functions
export const api = {
  // Authentication endpoints
  register: async (userData) => {
    const response = await apiClient.post('/auth/register', userData);
    return response.data;
  },

  login: async (credentials) => {
    const response = await apiClient.post('/auth/login', credentials);
    return response.data;
  },

  forgotPassword: async (email, formLink) => {
    // YPaaS API requires both email and form_link
    const response = await apiClient.post('/auth/forgot-password', { 
      email,
      form_link: formLink 
    });
    return response.data;
  },

  resetPassword: async (token, password) => {
    // YPaaS API expects 'password' not 'new_password'
    const response = await apiClient.post('/auth/reset-password', { 
      token, 
      password 
    });
    return response.data;
  },

  // User profile endpoints
  getProfile: async () => {
    const response = await apiClient.get('/users/profile');
    return response.data;
  },

  updateProfile: async (userData) => {
    const response = await apiClient.patch('/users/profile', userData);
    return response.data;
  },

  // Children management endpoints
  getChildren: async () => {
    const response = await apiClient.get('/users/children');
    return response.data;
  },

  addChild: async (childData) => {
    const response = await apiClient.post('/users/children', childData);
    return response.data;
  },

  updateChild: async (childId, childData) => {
    const response = await apiClient.patch(`/users/children/${childId}`, childData);
    return response.data;
  },

  deleteChild: async (childId) => {
    const response = await apiClient.delete(`/users/children/${childId}`);
    return response.data;
  },

  // NOTE: The following endpoints ARE AVAILABLE in YPaaS API
  
  // Programs endpoints - Available in YPaaS API
  getPrograms: async () => {
    // Unified: returns ALL programs (term-style and event-style). The UI
    // distinguishes them via feature flags (e.g. feature_scheduling).
    const response = await apiClient.get('/programs');
    return response.data || [];
  },

  getProgram: async (programId) => {
    // Authenticated endpoint for viewing program details
    const response = await apiClient.get(`/programs/${programId}`);
    return response.data;
  },

  getPublicPrograms: async () => {
    // Unified: all public programs (term-style and event-style).
    const response = await apiClient.get('/public/programs');
    return response.data || [];
  },

  getPublicProgram: async (programId) => {
    // Public endpoint for viewing public program details (no auth required)
    const response = await apiClient.get(`/public/programs/${programId}`);
    return response.data;
  },

  // Unified enrollment endpoint. Accepts either the legacy shape
  // ({ program_id, enrollee_ids: [...] }) or the special shape
  // ({ special_program_id, enrollments: [{ enrollee_id, custom_field_responses }] })
  // and normalizes to the unified payload { program_id, enrollees: [{ enrollee_id, custom_responses }] }.
  enrollInProgram: async (enrollmentData) => {
    const response = await apiClient.post('/programs/enrollments', normalizeEnrollPayload(enrollmentData));
    return response.data;
  },

  // Get user's enrollments - Available in YPaaS API
  getEnrollments: async () => {
    const response = await apiClient.get('/users/enrollments');
    return response.data;
  },

  // Sessions endpoints - Available in YPaaS API
  getSessions: async (programId) => {
    // GET /programs/:program_id/sessions - fetch all sessions for a program
    const response = await apiClient.get(`/programs/${programId}/sessions`);
    return response.data;
  },

  getSession: async (programId, sessionId) => {
    // GET /programs/:program_id/sessions/:id - fetch a specific session
    const response = await apiClient.get(`/programs/${programId}/sessions/${sessionId}`);
    return response.data;
  },

  createSession: async (programId, sessionData) => {
    // POST /programs/:program_id/sessions - create a new session (admin only)
    const response = await apiClient.post(`/programs/${programId}/sessions`, sessionData);
    return response.data;
  },

  updateSession: async (programId, sessionId, sessionData) => {
    // PATCH /programs/:program_id/sessions/:id - update a session (admin only)
    const response = await apiClient.patch(`/programs/${programId}/sessions/${sessionId}`, sessionData);
    return response.data;
  },

  deleteSession: async (programId, sessionId) => {
    // DELETE /programs/:program_id/sessions/:id - delete a session (admin only)
    const response = await apiClient.delete(`/programs/${programId}/sessions/${sessionId}`);
    return response.data;
  },

  // Admin - Program Management
  createProgram: async (programData) => {
    // POST /programs - create a new program (admin only)
    const response = await apiClient.post('/programs', programData);
    return response.data;
  },

  updateProgram: async (programId, programData) => {
    // PATCH /programs/:id - update a program (admin only)
    const response = await apiClient.patch(`/programs/${programId}`, programData);
    return response.data;
  },

  deleteProgram: async (programId) => {
    // DELETE /programs/:id - delete a program (admin only)
    const response = await apiClient.delete(`/programs/${programId}`);
    return response.data;
  },

  // Admin - Enrollment Viewing
  getProgramEnrollments: async (programId) => {
    // GET /programs/:id/enrollments - view enrollments for a program (admin only)
    const response = await apiClient.get(`/programs/${programId}/enrollments`);
    return response.data;
  },

  // Admin - User Management
  getUsersByRole: async (role) => {
    // GET /users?role=parent - get users by role (admin only)
    const response = await apiClient.get(`/users?role=${role}`);
    return response.data;
  },

  // Admin - Email Management
  sendGroupEmail: async (recipients, subject, body) => {
    // POST /email/group - send personalized email to multiple recipients (admin only)
    const response = await apiClient.post('/email/group', {
      recipients,
      subject,
      body
    });
    return response.data;
  },

  getEmailHistory: async (limit = 20, offset = 0) => {
    // GET /email/history - get paginated email history (admin only)
    const response = await apiClient.get('/email/history', { params: { limit, offset } });
    return response.data;
  },


  // NOTE: The following endpoints do NOT exist in YPaaS API
  // They are commented out until the backend is updated

  // Individual enrollment management - NOT AVAILABLE IN YPaaS API
  // getEnrollments: async () => {
  //   const response = await apiClient.get('/enrollments');
  //   return response.data;
  // },

  // Payment intent endpoints - NOT AVAILABLE IN YPaaS API
  // Payment is handled through the enrollment process
  // createPaymentIntent: async (paymentData) => {
  //   const response = await apiClient.post('/payments/create-intent', paymentData);
  //   return response.data;
  // },

  // confirmPayment: async (paymentIntentId, paymentMethodId) => {
  //   const response = await apiClient.post('/payments/confirm', {
  //     payment_intent_id: paymentIntentId,
  //     payment_method_id: paymentMethodId
  //   });
  //   return response.data;
  // },

  // Utility functions
  setAuthToken: (token) => {
    if (token) {
      localStorage.setItem('jwt_token', token);
    } else {
      localStorage.removeItem('jwt_token');
    }
  },

  getAuthToken: () => {
    return localStorage.getItem('jwt_token');
  },

  clearAuthToken: () => {
    localStorage.removeItem('jwt_token');
  }
};

export default api;